/**
 * The Inbox PWA's icon badge: the inbox count, cleared at zero (nav spec §4.9).
 *
 * Badge, not notifications — the owner keeps every notification off, so a
 * number on the home-screen icon is the one signal that is read when the owner
 * looks and never pushed. Only the Inbox app carries it: a count on the Tasks
 * icon would be about a different app. No service worker is involved, so the
 * badge is live while the app is open and goes stale once it is closed; stated
 * as acceptable in the spec.
 */
import { useEffect } from 'react';

import { useAppStore } from '@/stores/useAppStore';

import { useInstalledApp } from './useInstalledApp';

import type { AppSlug } from './installedApp';

/** The one installed app whose icon carries the inbox count. */
const BADGED_APP: AppSlug = 'attention';

/** The slice of `navigator` the badge needs; a navigator without it is a no-op. */
type BadgeNavigator = Partial<Pick<Navigator, 'setAppBadge' | 'clearAppBadge'>>;

/**
 * Set or clear the icon badge for `count`. Called on the navigator object
 * itself (never a detached method — the Badging API throws on a lost `this`).
 * `null` means the count has not been fetched yet: the icon keeps whatever it
 * shows rather than flashing to nothing on every launch. A rejected promise
 * (permission, platform) has nothing to do; it is swallowed.
 */
export function applyAppBadge(nav: BadgeNavigator, count: number | null): void {
  if (typeof nav.setAppBadge !== 'function' || typeof nav.clearAppBadge !== 'function') return;
  if (count === null) return;
  const result = count > 0 ? nav.setAppBadge(count) : nav.clearAppBadge();
  void result.catch(() => undefined);
}

/**
 * Keep the Inbox PWA's icon badge equal to the inbox count from the nav-count
 * store (fed by `useNavCounts`, which polls from the always-mounted rail).
 * Mount once in the authenticated layout; a no-op in every other app and in a
 * browser tab.
 */
export function useAppBadge(): void {
  const slug = useInstalledApp();
  const count = useAppStore(s => s.navCounts.emailInbox);
  useEffect(() => {
    if (slug !== BADGED_APP) return;
    applyAppBadge(navigator, count);
  }, [slug, count]);
}
